import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://tarelabs.com';
const DEFAULT_TITLE = 'Tare Labs — Integration & AI Automation for Logistics & eCommerce';
const DEFAULT_DESCRIPTION =
  'EDI, system integration, and AI automation for logistics and eCommerce companies — built by a solutions architect from Amazon, Flexport, and UPS.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * Per-page metadata + structured data.
 *
 * Title / meta / canonical / OG / Twitter go through react-helmet-async,
 * which prerenders correctly with react-snap.
 *
 * JSON-LD is rendered as a real <script> element in the component tree
 * (NOT inside <Helmet>). react-helmet-async can drop <script> children
 * during static snapshotting; emitting the script directly into the DOM
 * guarantees react-snap captures it into the static HTML. It is valid for
 * ld+json scripts to live in <body>; Google reads them anywhere.
 */
const Seo = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  type = 'website',
  image = DEFAULT_OG_IMAGE,
  jsonLd,
  noindex = false,
}) => {
  const fullTitle = title ? `${title} | Tare Labs` : DEFAULT_TITLE;
  const canonical = `${SITE_URL}${path === '/' ? '' : path}`;

  // Normalize to an array so callers can pass one object or several.
  const blocks = jsonLd
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : [];

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        {noindex && <meta name="robots" content="noindex,nofollow" />}

        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content={type} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={image} />
        <meta property="og:site_name" content="Tare Labs" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
      </Helmet>

      {blocks.map((block, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
        />
      ))}
    </>
  );
};

export default Seo;
